import {DetailedHTMLProps, ImgHTMLAttributes, ReactElement, useCallback} from 'react';
import {useEnvironmentStore} from '@di-shared/features/environment/environmentStore';
import {BrandType, ThemeType} from '@shared/cci/features/const/environment';
import {ErrorBoundary} from 'react-error-boundary';
import {useQuery} from '@tanstack/react-query';
import {getQueryKey} from '@shared/queryKeyMap';
import {DEFAULT_CACHE_TIME} from '@di-shared/const';

export type ImageSelectorWrapperProps = DetailedHTMLProps<ImgHTMLAttributes<HTMLImageElement>, HTMLImageElement> & {
  kiaLightSrc: () => Promise<{default: string}>;
  kiaDarkSrc: () => Promise<{default: string}>;
  hyundaiLightSrc: () => Promise<{default: string}>;
  hyundaiDarkSrc: () => Promise<{default: string}>;
  genesisLightSrc: () => Promise<{default: string}>;
  genesisDarkSrc: () => Promise<{default: string}>;
  fallback?: ReactElement;
};

function ImageSelectorWrapper({
  kiaLightSrc,
  kiaDarkSrc,
  hyundaiLightSrc,
  hyundaiDarkSrc,
  genesisLightSrc,
  genesisDarkSrc,
  fallback = <></>,
  ...props
}: ImageSelectorWrapperProps) {
  const theme = useEnvironmentStore((state) => state.theme);
  const brand = import.meta.env.VITE_BRAND;

  const selectImage = useCallback(async () => {
    let imgSrc;
    if (brand === BrandType.KIA) {
      if (theme === ThemeType.LIGHT) {
        imgSrc = await kiaLightSrc();
      } else {
        imgSrc = await kiaDarkSrc();
      }
    } else if (brand === BrandType.HYUNDAI) {
      if (theme === ThemeType.LIGHT) {
        imgSrc = await hyundaiLightSrc();
      } else {
        imgSrc = await hyundaiDarkSrc();
      }
    } else {
      if (theme === ThemeType.LIGHT) {
        imgSrc = await genesisLightSrc();
      } else {
        imgSrc = await genesisDarkSrc();
      }
    }
    return imgSrc.default;
  }, [theme]);

  return (
    <ErrorBoundary fallback={<></>}>
      {/* FIXME img tag에는 skeleton 마크업이 먹지 않음 */}
      <ImageSelector
        imageFetcher={selectImage}
        fallback={fallback}
        {...props}
      />
    </ErrorBoundary>
  );
}

export default ImageSelectorWrapper;

export type ImageSelectorProps = DetailedHTMLProps<ImgHTMLAttributes<HTMLImageElement>, HTMLImageElement> & {
  imageFetcher: () => Promise<string>;
  fallback: ReactElement;
};

function ImageSelector({imageFetcher, fallback, ...props}: ImageSelectorProps) {
  const theme = useEnvironmentStore((state) => state.theme);
  const brand = import.meta.env.VITE_BRAND;

  const {data: imgSrc} = useQuery({
    queryKey: [getQueryKey(imageFetcher), theme, brand, props.alt],
    queryFn: () => imageFetcher(),
    ...DEFAULT_CACHE_TIME,
  });

  if (!imgSrc) {
    return fallback;
  }

  return (
    <img
      {...props}
      src={imgSrc}
      aria-hidden="true"
    />
  );
}
